"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Bell } from "lucide-react";
import { Event } from "@/lib/types";
import { useAuth } from "./auth/auth-context";
import dynamic from "next/dynamic";

const SubscriptionModal = dynamic(() => import("./events/subscription-modal").then((module) => module.SubscriptionModal), { ssr: false });

interface SubscribeButtonProps {
  type: "artist" | "venue";
  targetId: number;
  targetName: string;
  targetImage?: string | null;
  size?: "sm" | "md";
  event?: Event;
  groupHover?: boolean;
}

export function SubscribeButton({
  type,
  targetId,
  targetName,
  targetImage,
  size = "sm",
  event,
  groupHover,
}: SubscribeButtonProps) {
  const t = useTranslations("Subscription");
  const {
    user,
    subscribedArtistIds,
    subscribedVenueIds,
    subscribedEventIds,
    subscribe,
    unsubscribe,
    openAuthModal,
  } = useAuth();

  const [modalOpen, setModalOpen] = useState(false);

  // When event is provided, bell is filled only when subscribed to the event itself
  const isSubscribed = event
    ? subscribedEventIds.has(event.id)
    : type === "artist"
      ? subscribedArtistIds.has(targetId)
      : subscribedVenueIds.has(targetId);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (event) {
      if (isSubscribed) {
        // Already following this event — unfollow directly
        unsubscribe("show", event.id);
      } else {
        // Not yet subscribed — open modal to choose what to follow
        setModalOpen(true);
      }
    } else {
      // Direct toggle (artist/venue page headers)
      if (!user) {
        openAuthModal([{ type, targetId, targetName, targetImage }]);
        return;
      }
      if (isSubscribed) {
        unsubscribe(type, targetId);
      } else {
        subscribe(type, targetId, targetName);
      }
    }
  };

  const iconSize = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  const btnSize =
    size === "sm"
      ? "h-10 w-10"
      : "h-10 w-10";

  return (
    <>
      <button
        data-subscribe-bell
        onClick={handleClick}
        className={`relative z-10 pointer-events-auto shrink-0 flex items-center justify-center rounded-full transition-all cursor-pointer ${btnSize} ${
          isSubscribed
            ? "bg-acid/20 text-acid shadow-[0_0_8px_rgba(209,248,5,0.3)] hover:bg-acid/30 hover:shadow-[0_0_12px_rgba(209,248,5,0.4)]"
            : `text-neutral-400 hover:text-acid ${groupHover ? "group-hover/follow:text-acid" : ""}`
        }`}
        title={
          isSubscribed
            ? event
              ? `${t("unfollow")} ${event.name}`
              : `${t("unfollow")} ${targetName}`
            : event
              ? `${t("follow")} ${event.name}`
              : `${t("follow")} ${targetName}`
        }
      >
        {isSubscribed ? (
          <Bell className={`${iconSize} fill-current`} />
        ) : (
          <Bell className={iconSize} />
        )}
      </button>
      {modalOpen && event && (
        <SubscriptionModal
          event={event}
          onClose={() => setModalOpen(false)}
          initialArtistId={type === "artist" ? targetId : undefined}
        />
      )}
    </>
  );
}
